import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { BrandingProvider } from '@/contexts/BrandingContext';
import { Toaster } from '@/components/ui/sonner';

// Layouts
import OperatorLayout from '@/components/layout/OperatorLayout';
import ClientLayout from '@/components/layout/ClientLayout';

// Auth pages
import Login from '@/pages/auth/Login';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import Signup from '@/pages/auth/Signup';
import MagicVerify from '@/pages/auth/MagicVerify';

// Operator pages
import Dashboard from '@/pages/operator/Dashboard';
import Inbox from '@/pages/operator/Inbox';
import Pipeline from '@/pages/operator/Pipeline';
import Clients from '@/pages/operator/Clients';
import ClientDetail from '@/pages/operator/ClientDetail';
import ProjectDetail from '@/pages/operator/ProjectDetail';
import Settings from '@/pages/operator/Settings';
import OperatorProfile from '@/pages/operator/Profile';
import Forms from '@/pages/operator/Forms';
import CalendarPage from '@/pages/operator/CalendarPage';
import DeliveryCalendar from '@/pages/operator/DeliveryCalendar';
import Wizards from '@/pages/operator/Wizards';
import Leads from '@/pages/operator/Leads';
import Today from '@/pages/operator/Today';
import KnowledgeBase from '@/pages/operator/KnowledgeBase';
import MeetingsPage from '@/pages/operator/Meetings';
import ReportsNps from '@/pages/operator/ReportsNps';
import Referrals from '@/pages/operator/Referrals';
import MiniSites from '@/pages/operator/MiniSites';
import AuditLog from '@/pages/operator/AuditLog';
import SearchPage from '@/pages/operator/SearchPage';
import Documents from '@/pages/operator/Documents';
import ZoomMeetings from '@/pages/operator/ZoomMeetings';

// Client pages
import ClientPortal from '@/pages/client/ClientPortal';
import ClientProject from '@/pages/client/ClientProject';
import ClientProfile from '@/pages/client/ClientProfile';
import PaymentSuccess from '@/pages/client/PaymentSuccess';

// Public pages (no auth)
import IntakeForm from '@/pages/public/IntakeForm';
import BookMeeting from '@/pages/public/BookMeeting';
import Wizard from '@/pages/public/Wizard';
import VaultSubmit from '@/pages/public/VaultSubmit';
import NpsAnswer from '@/pages/public/NpsAnswer';
import MiniSite from '@/pages/public/MiniSite';
import Landing from '@/pages/public/Landing';
import Features from '@/pages/public/Features';
import FreeAudit from '@/pages/public/FreeAudit';

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#006080', borderTopColor: 'transparent' }} />
      <p className="text-sm" style={{ color: 'var(--muted)' }}>Loading...</p>
    </div>
  </div>
);

function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === 'super_admin' ? '/admin' : '/portal'} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Landing />;
  if (user.role === 'super_admin') return <Navigate to="/admin" replace />;
  return <Navigate to="/portal" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/features" element={<Features />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<Signup />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/magic" element={<MagicVerify />} />

      {/* Public (no auth) */}
      <Route path="/audit" element={<FreeAudit />} />
      <Route path="/free-audit" element={<FreeAudit />} />
      <Route path="/intake/:slug" element={<IntakeForm />} />
      <Route path="/book/:operatorId" element={<BookMeeting />} />
      <Route path="/w/:slug" element={<Wizard />} />
      <Route path="/wizard/:slug" element={<Wizard />} />
      <Route path="/vault-submit/:token" element={<VaultSubmit />} />
      <Route path="/nps/:token" element={<NpsAnswer />} />
      <Route path="/sites/:brandSlug" element={<MiniSite />} />

      {/* Operator shell */}
      <Route path="/admin" element={
        <ProtectedRoute role="super_admin">
          <OperatorLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:clientId" element={<ClientDetail />} />
        <Route path="clients/:clientId/projects/:projectId" element={<ProjectDetail />} />
        <Route path="forms" element={<Forms />} />
        <Route path="forms/:formId" element={<Forms />} />
        <Route path="wizards" element={<Wizards />} />
        <Route path="leads" element={<Leads />} />
        <Route path="today" element={<Today />} />
        <Route path="kb" element={<KnowledgeBase />} />
        <Route path="meetings" element={<MeetingsPage />} />
        <Route path="reports" element={<ReportsNps />} />
        <Route path="referrals" element={<Referrals />} />
        <Route path="minisites" element={<MiniSites />} />
        <Route path="documents" element={<Documents />} />
        <Route path="zoom" element={<ZoomMeetings />} />
        <Route path="audit" element={<AuditLog />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="delivery" element={<DeliveryCalendar />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/:tab" element={<Settings />} />
        <Route path="profile" element={<OperatorProfile />} />
      </Route>

      {/* Client portal */}
      <Route path="/portal" element={
        <ProtectedRoute>
          <ClientLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ClientPortal />} />
        <Route path="projects/:projectId" element={<ClientProject />} />
        <Route path="payment-success" element={<PaymentSuccess />} />
        <Route path="payment-cancel" element={<PaymentSuccess />} />
        <Route path="profile" element={<ClientProfile />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </AuthProvider>
    </BrandingProvider>
  );
}

export default App;
